import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { api } from '@/api/repository/axios';
import { isLoggedInAtom } from '@/state/loginState';

/**
 * 목록(채용·세미나)에서 저장한 조건 — 회원·목록당 하나.
 * 저장한 회원이 목록에 들어오면 그 조건이 켜진 채로 보인다(주소에 조건이 있으면 주소가 먼저다).
 * 한 번 읽어 화면들이 같이 쓴다(목록이 들어올 때 읽고, 필터 창의 저장 버튼이 바꾼다). 로그아웃하면 비운다.
 */
export type SavedFilterDomain = 'recruitment' | 'seminar';

export interface SavedListFilter {
    /** 목록 주소의 쿼리 문자열(정렬·검색어 제외) */
    query: string;
    /** 조건에 맞는 새 글 알림을 받는지 */
    notify: boolean;
}

type Filters = Record<SavedFilterDomain, SavedListFilter | null>;

interface State {
    status: 'idle' | 'loading' | 'ready';
    filters: Filters;
}

const EMPTY: Filters = { recruitment: null, seminar: null };

let state: State = { status: 'idle', filters: EMPTY };
/** 마지막 조회 번호 — 로그아웃 뒤 늦게 온 응답은 버린다 */
let loadSeq: number = 0;
const listeners: Set<() => void> = new Set();

const setState = (next: State): void => {
    state = next;
    listeners.forEach((listener: () => void) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const getState = (): State => state;

const load = (): void => {
    if (state.status !== 'idle') return;
    const seq: number = ++loadSeq;
    setState({ status: 'loading', filters: EMPTY });
    api.user.getSavedFilters()
        .then((response) => {
            if (seq !== loadSeq) return;
            const result = response.code === 0 ? response.result : null;
            setState({
                status: 'ready',
                filters: {
                    recruitment: result?.recruitment ? { query: result.recruitment.query, notify: result.recruitment.notify } : null,
                    seminar: result?.seminar ? { query: result.seminar.query, notify: result.seminar.notify } : null,
                },
            });
        })
        // 못 읽으면 저장한 조건이 없는 것으로 본다(목록은 기본 조건으로 그대로 보인다)
        .catch(() => {
            if (seq === loadSeq) setState({ status: 'ready', filters: EMPTY });
        });
};

const reset = (): void => {
    loadSeq += 1;
    if (state.status !== 'idle') setState({ status: 'idle', filters: EMPTY });
};

export interface SavedListFilterControls {
    loggedIn: boolean;
    /** 저장한 조건을 읽었는지 — false 인 동안은 [saved] 를 아직 모른다 */
    ready: boolean;
    saved: SavedListFilter | null;
    /** 저장(덮어쓰기) — 실패하면 던진다 */
    save: (query: string, params: Record<string, string[]>, notify: boolean) => Promise<void>;
    /** 저장 해제 — 실패하면 던진다 */
    remove: () => Promise<void>;
}

export const useSavedListFilter = (domain: SavedFilterDomain): SavedListFilterControls => {
    const loggedIn: boolean = useRecoilValue(isLoggedInAtom);
    const snapshot: State = useSyncExternalStore(subscribe, getState);

    useEffect(() => {
        if (loggedIn) load();
        else reset();
    }, [loggedIn]);

    const save = useCallback(async (query: string, params: Record<string, string[]>, notify: boolean): Promise<void> => {
        const response = await api.user.saveFilter(domain, { query, params, notify });
        if (response.code !== 0) throw new Error(response.message || 'save failed');
        setState({ status: 'ready', filters: { ...state.filters, [domain]: { query: response.result.query, notify: response.result.notify } } });
    }, [domain]);

    const remove = useCallback(async (): Promise<void> => {
        const response = await api.user.removeSavedFilter(domain);
        if (response?.code !== 0) throw new Error(response?.message || 'remove failed');
        setState({ status: 'ready', filters: { ...state.filters, [domain]: null } });
    }, [domain]);

    return {
        loggedIn,
        ready: loggedIn && snapshot.status === 'ready',
        saved: loggedIn ? snapshot.filters[domain] : null,
        save,
        remove,
    };
};

/** 목록이 조건을 주소에 쓸 때 같이 남기는 표시 — 이 방문 기록 칸에서는 조건을 직접 바꿨다(저장한 조건을 다시 켜지 않는다) */
export const FILTER_TOUCHED_STATE: { filterTouched: true } = { filterTouched: true };

/**
 * 조건 없이 목록에 들어왔을 때 저장한 조건을 켠다 — 메뉴로 들어올 때마다(같은 화면에서 메뉴를 다시 눌러도) 한 번.
 * 주소에 조건이 있거나, 이 칸에서 조건을 직접 바꿨거나(전부 풀어 조건 없는 주소가 돼도), 뒤로 와서 보던 목록을 되살린 경우에는 켜지 않는다.
 * 메뉴 주소와 조건을 다 푼 주소가 같아서(채용 `category=REGION&districts=0_0`), 직접 바꾼 것은 방문 기록 칸의 표시([FILTER_TOUCHED_STATE])로 가른다.
 *
 * @param plain 주소에 걸린 조건이 없고(탭·정렬만), 되살린 목록도 아닌지
 * @param apply 저장한 주소 쿼리를 목록에 적용한다(주소를 [FILTER_TOUCHED_STATE] 와 함께 바꾼다). 들어온 탭과 안 맞으면 아무것도 안 해도 된다
 * @returns waiting — 저장한 조건을 읽는 중이라 아직 어떤 목록을 보여 줄지 모른다(기본 목록을 잠깐 비추지 않게 로딩으로 둔다)
 */
export const useSavedFilterOnEntry = (domain: SavedFilterDomain, plain: boolean, apply: (savedQuery: string) => void): { waiting: boolean } => {
    const { loggedIn, ready, saved } = useSavedListFilter(domain);
    const location = useLocation();
    const touched: boolean = Boolean((location.state as { filterTouched?: boolean } | null)?.filterTouched);
    const eligible: boolean = plain && !touched;
    const savedRef = useRef<SavedListFilter | null>(saved);
    savedRef.current = saved;
    const applyRef = useRef(apply);
    applyRef.current = apply;

    // 저장 버튼으로 조건을 막 저장했을 때는 다시 돌지 않는다(saved 는 ref 로만 본다) — 들어올 때만 켠다
    useEffect(() => {
        if (!eligible || !ready || !savedRef.current) return;
        applyRef.current(savedRef.current.query);
    }, [location.key, eligible, ready]);

    return { waiting: loggedIn && !ready && eligible };
};
